/**
 * A running record of the page's viewport geometry, for chasing layout that
 * shifts while the iOS shell is in use (the header gaining a status bar's
 * height until the app is killed). Started once at boot; `/admin/viewport`
 * reads it.
 *
 * A snapshot is taken on every event that could move the viewport and kept
 * only when it differs from the last one, so the log reads as "what changed,
 * and right after what". In memory only: the state being chased survives
 * until the app is killed, and so does this.
 */

export interface ViewportSnapshot {
    at: number
    /** What prompted the snapshot: an event name, or a route change. */
    cause: string
    path: string
    innerW: number
    innerH: number
    clientH: number
    screenH: number
    vvH: number | null
    vvOffsetTop: number | null
    vvPageTop: number | null
    vvScale: number | null
    scrollY: number
    /** env(safe-area-inset-*) as CSS resolves it right now. */
    safeTop: number
    safeBottom: number
    /** The nav bar's top and height in the viewport. */
    navTop: number | null
    navH: number | null
    visibility: DocumentVisibilityState
    focused: string | null
}

const MAX = 200
const log: ViewportSnapshot[] = []
const listeners = new Set<() => void>()
let probe: HTMLDivElement | null = null
let started = false

function safeArea(): { top: number; bottom: number } {
    if (!probe) {
        probe = document.createElement('div')
        probe.setAttribute('aria-hidden', 'true')
        probe.style.cssText =
            'position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
            'padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)'
        document.body.appendChild(probe)
    }
    const cs = getComputedStyle(probe)
    return { top: parseFloat(cs.paddingTop) || 0, bottom: parseFloat(cs.paddingBottom) || 0 }
}

function describeFocus(): string | null {
    const el = document.activeElement
    if (!el || el === document.body) return null
    const tag = el.tagName.toLowerCase()
    const type = el.getAttribute('type')
    return type ? `${tag}[${type}]` : tag
}

export function snapshotViewport(cause: string): ViewportSnapshot {
    const vv = window.visualViewport
    const nav = document.querySelector('nav')?.getBoundingClientRect() ?? null
    const safe = safeArea()
    const r = (n: number) => Math.round(n * 10) / 10
    return {
        at: Date.now(),
        cause,
        path: location.pathname,
        innerW: window.innerWidth,
        innerH: window.innerHeight,
        clientH: document.documentElement.clientHeight,
        screenH: window.screen.height,
        vvH: vv ? r(vv.height) : null,
        vvOffsetTop: vv ? r(vv.offsetTop) : null,
        vvPageTop: vv ? r(vv.pageTop) : null,
        vvScale: vv ? r(vv.scale) : null,
        scrollY: r(window.scrollY),
        safeTop: r(safe.top),
        safeBottom: r(safe.bottom),
        navTop: nav ? r(nav.top) : null,
        navH: nav ? r(nav.height) : null,
        visibility: document.visibilityState,
        focused: describeFocus(),
    }
}

/** The fields whose change is worth a log line. Scroll alone is not. */
function layoutKey(s: ViewportSnapshot): string {
    const navTopAtRest = s.navTop == null ? null : Math.round(s.navTop + s.scrollY)
    return [
        s.innerW, s.innerH, s.clientH, s.vvH, s.vvOffsetTop, s.vvScale,
        s.safeTop, s.safeBottom, navTopAtRest, s.navH, s.visibility,
    ].join('|')
}

function record(cause: string) {
    const snap = snapshotViewport(cause)
    const last = log[log.length - 1]
    if (last && layoutKey(last) === layoutKey(snap)) return
    log.push(snap)
    if (log.length > MAX) log.shift()
    listeners.forEach((fn) => fn())
}

export function viewportLog(): readonly ViewportSnapshot[] {
    return log
}

export function subscribeViewportLog(fn: () => void): () => void {
    listeners.add(fn)
    return () => listeners.delete(fn)
}

/** Note a route change, so a shift can be placed against navigation. */
export function noteViewportRoute(path: string) {
    if (started) record(`route ${path}`)
}

export function startViewportLog(): void {
    if (started || typeof window === 'undefined') return
    started = true
    // Settled values, not mid-animation ones: the keyboard and the resize
    // both report several times while they move.
    let pending: number | null = null
    const later = (cause: string) => () => {
        if (pending != null) clearTimeout(pending)
        pending = window.setTimeout(() => {
            pending = null
            record(cause)
        }, 250)
    }
    window.addEventListener('resize', later('resize'))
    window.addEventListener('orientationchange', later('orientationchange'))
    window.addEventListener('pageshow', later('pageshow'))
    window.visualViewport?.addEventListener('resize', later('vv-resize'))
    document.addEventListener('visibilitychange', later(`visibility`))
    document.addEventListener('focusin', later('focusin'))
    document.addEventListener('focusout', later('focusout'))
    window.addEventListener('load', later('load'))
    record('start')
}
